/**
 * Build functions for EditVendorPageV2 component
 */
import { getDocumentType } from './EditVendorPageV2.helpers';

/**
 * Helper to build PAN document info
 */
const buildPANDocument = (formValues) => {
	if (!formValues.pan_file_key || !formValues.pan_number) {
		return null;
	}
	return {
		document_key: formValues.pan_file_key,
		document_type: getDocumentType(formValues.pan_file_key),
		number: formValues.pan_number,
	};
};

/**
 * Helper to map GST type to API format
 */
const mapGSTType = (gstType) => {
	const gstTypeMap = {
		Registered: 'Registered',
		Composition: 'Composition',
		Unregistered: 'Unregistered',
	};
	return gstTypeMap[gstType] || gstType || '';
};

/**
 * Helper to build GST document info
 */
const buildGSTDocument = (formValues) => {
	if (!formValues.gst_type) {
		return null;
	}

	const gstType = mapGSTType(formValues.gst_type);
	const gstDoc = { gst_type: gstType };

	// Only include number and document_key if GST type is NOT Unregistered
	if (formValues.gst_type !== 'Unregistered') {
		if (formValues.gst_file_key) {
			gstDoc.document_key = formValues.gst_file_key;
			gstDoc.document_type = getDocumentType(formValues.gst_file_key);
		}
		if (formValues.gst_number) {
			gstDoc.number = formValues.gst_number;
		}
	}

	return gstDoc;
};

/**
 * Helper to build Aadhar document info
 */
const buildAadharDocument = (formValues) => {
	if (!formValues.aadhar_file_key || !formValues.aadhar_number) {
		return null;
	}
	return {
		document_key: formValues.aadhar_file_key,
		number: formValues.aadhar_number,
	};
};

/**
 * Helper to build UDF document info (VRF document is independent of MSME)
 */
const buildUDFDocument = (formValues) => {
	if (!formValues.vrf_document_key) {
		return null;
	}
	return {
		document_key: formValues.vrf_document_key,
	};
};

/**
 * Helper to build document_info object
 */
export const buildDocumentInfo = (formValues) => {
	if (!formValues || typeof formValues !== 'object') {
		return {};
	}

	const document_info = {};

	const panDoc = buildPANDocument(formValues);
	if (panDoc) {
		document_info.pan = panDoc;
	}

	const gstDoc = buildGSTDocument(formValues);
	if (gstDoc) {
		document_info.gst = gstDoc;
	}

	const aadharDoc = buildAadharDocument(formValues);
	if (aadharDoc) {
		document_info.aadhar = aadharDoc;
	}

	const udfDoc = buildUDFDocument(formValues);
	if (udfDoc) {
		document_info.udf = udfDoc;
	}

	return document_info;
};

/**
 * Convert tds_applicable from 'Yes'/'No' string to boolean
 */
export const convertTdsApplicableToBoolean = (value) =>
	value === true || value === 'Yes' || value === 'yes';

/**
 * Build bank address object if address fields are present
 */
export const buildBankAddress = (formValues) => {
	if (
		!formValues.vendor_address &&
		!formValues.pincode &&
		!formValues.city &&
		!formValues.state
	) {
		return null;
	}

	return {
		address_line1: formValues.vendor_address || '',
		address_line2: '',
		pincode: formValues.pincode || '',
		city: formValues.city || '',
		state: formValues.state || '',
	};
};

/**
 * Build bank document info object
 */
export const buildBankDocumentInfo = (formValues) => {
	if (!formValues.bank_document_key) {
		return {};
	}

	return {
		branch_document: {
			document_key: formValues.bank_document_key,
		},
	};
};

/**
 * Build banking info object for API
 */
export const buildBankingInfo = (formValues) => {
	if (!formValues || typeof formValues !== 'object') {
		return {
			bank_address: {},
			document_info: {},
		};
	}

	const banking_info = {
		bank_name: formValues.bank_name || '',
		account_number: formValues.account_number || '',
		ifsc_code: formValues.ifsc_code || '',
		account_holder_name: formValues.account_holder_name || '',
		branch_name: formValues.branch_name || '',
		account_type: formValues.account_type || '',
		tds_applicable: convertTdsApplicableToBoolean(formValues.tds_applicable),
		document_info: buildBankDocumentInfo(formValues),
	};

	const bankAddress = buildBankAddress(formValues);
	if (bankAddress) {
		banking_info.bank_address = bankAddress;
	}

	return banking_info;
};

/**
 * Build contact_info structure for API
 * API expects both flat fields (primary_poc_name) AND nested structure (contact_info.primary.name)
 */
const buildContactInfo = (formValues) => {
	const contactInfo = {};

	// Primary contact (always include, even if fields are empty)
	contactInfo.primary = {
		name: formValues.primary_poc_name || '',
		contact: formValues.primary_poc_contact || '',
	};

	// Alternative contact (only include if at least one field has a value)
	if (formValues.alternative_poc_name || formValues.alternative_poc_contact) {
		contactInfo.alternative = {
			name: formValues.alternative_poc_name || '',
			contact: formValues.alternative_poc_contact || '',
		};
	}

	return contactInfo;
};

/**
 * Build vendor_info structure for API
 * API expects nested structure for vendor_info.address
 */
const buildVendorInfo = (formValues) => {
	const vendorInfo = {};

	// Address in vendor_info structure
	if (
		formValues.vendor_address ||
		formValues.pincode ||
		formValues.city ||
		formValues.state ||
		formValues.zone
	) {
		vendorInfo.address = {
			address_line: formValues.vendor_address || '',
			pincode: formValues.pincode || '',
			city: formValues.city || '',
			state: formValues.state || '',
			...(formValues.zone && { zone: formValues.zone }),
		};
	}

	// Email in vendor_info structure (if needed)
	if (formValues.vendor_email_id || formValues.vendor_email) {
		vendorInfo.email = formValues.vendor_email_id || formValues.vendor_email || '';
	}

	return Object.keys(vendorInfo).length > 0 ? vendorInfo : {};
};

/**
 * Build basic vendor info for API
 * Note: vendor_registered_msme and fds_applicable are part of Banking Information, not Basic Information
 *
 * IMPORTANT: API expects nested structures for contact info:
 * - contact_info.primary.name, contact_info.primary.contact (required structure)
 * - contact_info.alternative.name, contact_info.alternative.contact (optional)
 * - vendor_address (flat structure for address)
 * - vendor_info.address (nested structure for address - also included)
 */
export const buildBasicVendorInfo = (formValues, vendorId) => {
	const basicInfo = {
		id: vendorId,
		vendor_name: formValues.vendor_name || '',
		vendor_type: formValues.vendor_type || '',
		legal_structure: formValues.legal_structure || '',
		vendor_email_id: formValues.vendor_email_id || formValues.vendor_email || '',
		vendor_address: {
			address_line: formValues.vendor_address || '',
			pincode: formValues.pincode || '',
			...(formValues.zone && { zone: formValues.zone }),
			state: formValues.state || '',
			city: formValues.city || '',
		},
		business_unit_id: formValues.business_unit_id || 5,
	};

	// Add contact_info nested structure (required by API)
	// Always include contact_info with primary contact structure
	basicInfo.contact_info = buildContactInfo(formValues);

	// Add vendor_info nested structure (for address and email)
	const vendorInfo = buildVendorInfo(formValues);
	if (Object.keys(vendorInfo).length > 0) {
		basicInfo.vendor_info = vendorInfo;
	}

	return basicInfo;
};

/**
 * Build optional contact fields (DEPRECATED - use contact_info nested structure instead)
 * This function is kept for backward compatibility but should not be used
 * Alternative contact is now included in contact_info.alternative via buildContactInfo()
 */
export const buildOptionalContactFields = () => ({});

/**
 * Build MSME fields if applicable
 */
export const buildMSMEFields = (formValues) => {
	// API requires msme_number when vendor_registered_msme is true
	if (formValues.vendor_registered_msme) {
		return { msme_number: formValues.msme_number || '' };
	}
	return {};
};

/**
 * Build vehicle data for API
 */
export const buildVehicleData = (formValues) => ({
	total_vehicles: formValues.total_vehicles || '',
	vehicle_types: formValues.vehicle_types || [],
	owns_vehicles: formValues.owns_vehicles !== undefined ? formValues.owns_vehicles : true,
	vehicles: formValues.vehicles || [],
});

/**
 * Build serviceability data for API
 */
export const buildServiceabilityData = (formValues) => ({
	serviceable_cities: formValues.serviceable_cities || [],
	serviceable_states: formValues.serviceable_states || [],
	operational_hours_from: formValues.operational_hours_from || '',
	operational_hours_to: formValues.operational_hours_to || '',
	routes: formValues.routes || [],
});

/**
 * Build banking information data for API (step 2)
 * document_info (including UDF/VRF) is always included when present
 */
const buildBankingInfoData = (formValues, baseData) => {
	const bankingData = {
		...baseData,
		vendor_registered_msme: formValues.vendor_registered_msme || false,
		fds_applicable: formValues.tds_applicable || false,
		...buildMSMEFields(formValues),
		banking_info: buildBankingInfo(formValues) || {},
	};

	// Include document_info (PAN, GST, Aadhar, UDF/VRF) when present
	const documentInfo = buildDocumentInfo(formValues);
	if (documentInfo && Object.keys(documentInfo).length > 0) {
		bankingData.document_info = documentInfo;
	}

	return bankingData;
};

/**
 * Build API data for a specific step (partial update)
 */
export const buildAPIDataForStep = (formValues, step, vendorId) => {
	if (!formValues || typeof formValues !== 'object') {
		return {};
	}

	if (vendorId === undefined || vendorId === null || String(vendorId).trim() === '') {
		return {};
	}

	const baseData = {
		id: vendorId,
	};

	switch (step) {
		case 0: // Basic Information
			return {
				...baseData,
				...buildBasicVendorInfo(formValues, vendorId),
				// Note: contact_info is included in buildBasicVendorInfo() via buildContactInfo()
				// Note: MSME fields (vendor_registered_msme, msme_number) are part of Banking Information (step 2)
			};
		case 1: // Business Information
			return {
				...baseData,
				document_info: buildDocumentInfo(formValues) || {},
			};
		case 2: // Banking Information
			return buildBankingInfoData(formValues, baseData);
		case 3: // Vehicle Information
			return {
				...baseData,
				...buildVehicleData(formValues),
			};
		case 4: // Serviceability Info
			return {
				...baseData,
				...buildServiceabilityData(formValues),
			};
		default:
			// If no step specified, send all data (shouldn't happen in edit mode)
			return {
				...baseData,
				...buildBasicVendorInfo(formValues, vendorId),
				// Note: contact_info is included in buildBasicVendorInfo() via buildContactInfo()
				...buildMSMEFields(formValues),
				document_info: buildDocumentInfo(formValues) || {},
				banking_info: buildBankingInfo(formValues) || {},
			};
	}
};

/**
 * Transform form values to API request format
 */
export const transformFormDataToAPI = (formValues, step, vendorId) => {
	if (!formValues || typeof formValues !== 'object') {
		throw new Error('Invalid form values');
	}

	if (vendorId === undefined || vendorId === null || String(vendorId).trim() === '') {
		throw new Error('Invalid vendor ID');
	}

	// In edit mode with a specific step, only send data for that step (partial update)
	if (step !== null && step !== undefined) {
		return buildAPIDataForStep(formValues, step, vendorId);
	}

	// Otherwise, send all data (create mode or full update)
	return {
		...buildBasicVendorInfo(formValues, vendorId),
		// Note: contact_info is included in buildBasicVendorInfo() via buildContactInfo()
		...buildMSMEFields(formValues),
		document_info: buildDocumentInfo(formValues) || {},
		banking_info: buildBankingInfo(formValues) || {},
	};
};
